import {gql, useLazyQuery} from "@apollo/client";
import {useLogger} from "./useLogger";
import {getBrowser} from "../common/common";
import {ResortsContext} from "../store/providers/ResortsProvider";
import {useContext, useEffect, useMemo} from "react";
import {LocaleContext} from "../store/providers/LocaleProvider";

const QUERY = gql`
    query getResortDetail($hapimagResortId: Int!, $locale: String!) {
        resortCollection(limit: 1, where: { hapimagResortId: $hapimagResortId }, locale: $locale) {
            items {
                hapimagResortId
                isOnlineBookable
                resortName
                slug
                tourismTax
                houseboat
                reservationSections
                destination {
                    destination
                    slug
                    country {
                        country
                        slug
                    }
                }
                resortInfo {
                    checkInTime
                    checkOutTime
                    openingHoursCollection {
                        items {
                            weekdays
                            openingHoursMorningFrom
                            openingHoursMorningTo
                            openingHoursAfternoonFrom
                            openingHoursAfternoonTo
                            allDayOpen24H
                        }
                    }
                    receptionInfo {
                        informationDisplayed
                    }
                    lateCheckIn {
                        informationDisplayed
                    }
                    cancellationPolicy {
                        informationDisplayed {
                            json
                        }
                    }
                }
                arrivalLocation {
                    streetPostalCodeCityCountry
                    resortGooglePlaceId
                }
                resortContactDetails {
                    resortEmailAddress
                    resortPhoneNumber
                    host {
                        firstAndLastName
                        role
                        gender
                        picture {
                            url
                        }
                    }
                }
                payment {
                    paymentInformation
                    paymentInformationWithPointsPurchase
                }
                resortAdditionalWishesCollection {
                    items {
                        additionalWishesEntry
                        additionalWishesDescription
                        sihotCommentIds
                        icon {
                            url
                        }
                        shortcode
                    }
                }
                localChargesType
                preArrivalImportantDescription {
                    json
                }
                preArrivalImportantCta{
                    url
                    label
                    type
                }
            }
        }
    }
`;

export const useResortDetailLazy = () => {
    const { localeUppercaseUnderscore } = useContext(LocaleContext)[0];
    const [resortsStateAndIds, resortsDispatch] = useContext(ResortsContext);
    const { resorts, resortsLoading } = resortsStateAndIds;
    const { logger } = useLogger();
    const [getResults, { loading, data, error }] = useLazyQuery(QUERY, {
        fetchPolicy: getBrowser() === "Safari" ? "network-only" : "cache-first",
        errorPolicy: "all",
    });

    useEffect(() => {
        if (resorts && !resorts.length && !resortsLoading) {
            resortsDispatch({
                type: "GET_RESORTS_INIT",
                payload: { locale: localeUppercaseUnderscore, is_bookable: true },
            });
        }
    }, [resorts]);

    const results = data?.resortCollection?.items[0];

    if (error?.message) {
        logger(error?.message, "error", "useResortDetailLazy", error?.stack);
    }
    return useMemo(
        () => ({
            getResortDetails: getResults,
            resortDetails: results
                ? {
                    ...results,
                    isPartner: resorts.find((r) => r.hapimagResortId === results?.hapimagResortId)?.isPartner,
                }
                : undefined,
            loadingResortDetails: loading || resortsLoading,
            errorResortDetails: error,
        }),
        [results, loading, error, resorts, resortsLoading]
    );
};
